import type { ComponentType, ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { Check } from "lucide-react";
import { useAuthGate } from "@/components/auth/auth-gate";
import { NeuralButton } from "@/components/app/neural-button";

/** Primary CTA that opens the free-account modal for guests. */
export function StartFreeButton({
  label = "Start free",
  className = "",
}: {
  label?: string;
  className?: string;
}) {
  const { isAuthenticated, openAuthModal } = useAuthGate();
  if (isAuthenticated) {
    return (
      <Link
        to="/"
        className={`neural-gradient neural-halo relative isolate z-0 inline-flex h-13 items-center justify-center rounded-full px-6 text-[15px] font-bold tracking-tight text-white shadow-[0_18px_40px_-18px_rgba(0,0,0,0.9)] transition-transform active:scale-95 ${className}`}
      >
        Open your dashboard
      </Link>
    );
  }
  return (
    <NeuralButton type="button" onClick={() => openAuthModal()} className={className}>
      {label}
    </NeuralButton>
  );
}

export function MarketingSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ type: "spring", stiffness: 220, damping: 28 }}
      className="mt-12"
    >
      <h2 className="text-[22px] font-extrabold tracking-tight">{title}</h2>
      <div className="mt-4">{children}</div>
    </motion.section>
  );
}

export function FeatureCard({
  icon: Icon,
  title,
  body,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  body: string;
}) {
  return (
    <motion.div
      whileHover={{ y: -3 }}
      transition={{ type: "spring", stiffness: 300, damping: 24 }}
      className="glass rounded-[26px] border-hairline p-5"
    >
      <div className="flex items-center gap-3">
        <span className="neural-gradient neural-halo relative isolate z-0 grid size-10 shrink-0 place-items-center rounded-2xl">
          <Icon className="size-4 text-white" />
        </span>
        <h3 className="text-[16px] font-bold tracking-tight">{title}</h3>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </motion.div>
  );
}

export function CheckList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2.5 text-sm">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2.5">
          <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-surface">
            <Check className="size-3 text-primary" />
          </span>
          <span className="text-muted-foreground">{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function ClosingCta({
  title = "Start tracking your JEE preparation",
  body = "Free to start. Create an account when you want to save and sync your progress.",
}: {
  title?: string;
  body?: string;
}) {
  return (
    <div className="glass relative mt-12 overflow-hidden rounded-[28px] border-hairline p-6 text-center">
      <div
        aria-hidden
        className="neural-gradient pointer-events-none absolute -top-16 left-1/2 size-40 -translate-x-1/2 rounded-full opacity-25 blur-3xl"
      />
      <h2 className="relative text-[20px] font-extrabold tracking-tight">{title}</h2>
      <p className="relative mt-2 text-sm text-muted-foreground">{body}</p>
      <StartFreeButton className="mt-5" label="Create your free account" />
    </div>
  );
}

export function MarketingPage({ children }: { children: ReactNode }) {
  return (
    <div className="relative">
      <div aria-hidden className="aurora pointer-events-none fixed inset-0 -z-10" />
      <motion.main
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 240, damping: 28 }}
        className="mx-auto w-full max-w-2xl px-5 pb-16 pt-10 lg:max-w-5xl lg:px-8 2xl:max-w-6xl"
      >
        {children}
      </motion.main>
    </div>
  );
}
